/**
 * What a person should know before a key's value leaves Papan's hands — read by
 * the delete confirm (one key) and the leave-org notice (each key they own):
 * who might have it (the exposure headline) and how to revoke it where it was
 * issued (RevokeGuidance). Deleting the entry alone doesn't stop the value working.
 */
import { useTranslation } from '@papan/i18n'
import { keybookApi, type KeybookExposure } from '../client/api.js'
import { ExposureHeadline } from './ExposureHeadline.js'
import { RevokeGuidance } from './RevokeGuidance.js'

/** A key's exposure for the delete confirm: an org keybook's only; a failed read
 *  is null (the line is left out, never a reason to refuse the delete). */
export async function loadDeleteExposure(entryId: string, isOrg: boolean): Promise<KeybookExposure | null> {
  if (!isOrg) return null
  return keybookApi.getEntryExposure(entryId, { fresh: true }).catch(() => null)
}

/** One key's exposure line + how to revoke it. `exposure` undefined = not loaded
 *  here (null = none to show: a personal keybook, or the read failed). */
export function KeyRevokeSummary({ credentialType, provider, exposure }: {
  credentialType?: string | null
  provider?: string | null
  exposure?: KeybookExposure | null
}) {
  return (
    <div className="space-y-1">
      {exposure && <ExposureHeadline data={exposure} />}
      <RevokeGuidance credentialType={credentialType} provider={provider} />
    </div>
  )
}

/** The delete confirm's body for one key. The caller reads the exposure BEFORE
 *  the confirm opens (`loadDeleteExposure`), so Delete is never pressed ahead of
 *  the line; null = none to show (a personal keybook, or the read failed — the
 *  delete itself still asks). */
export function KeyDeleteNotice({ credentialType, provider, exposure }: {
  credentialType?: string | null
  provider?: string | null
  exposure: KeybookExposure | null
}) {
  const { t } = useTranslation('keybook')
  return (
    <div className="space-y-2 text-left" data-testid="keybook-delete-notice">
      <p>{t('revoke.deleteOnlyCopy')}</p>
      <KeyRevokeSummary credentialType={credentialType} provider={provider} exposure={exposure} />
    </div>
  )
}
